import {get, isEmpty, merge, omit, pick, set} from 'lodash';
import {useCallback, useLayoutEffect, useMemo, useRef, useState} from 'react';
import {useLocation} from 'react-router';

import {Probe, ProbeProcessor, ProbeStage} from '../types';

type UsePreservedStageOptionsArgs = {
  enabled?: boolean;
  filters: any;
  probe: Probe;
  processor: ProbeProcessor;
  stage: ProbeStage;
  updateFilters: (filters: any, opts?: any) => void;
}

const savedOptions = {};

const probeTimeOptionFields: string[] = ['timeSeriesDuration', 'timeSeriesAggregation'];
const stageOptionFields: string[] = [
  'aggregationType', 'combineGraphs', 'dataSource', 'filter', 'showContextInfo', 'valueColumnName', 'anomalousOnly'
];

export const useLastUsedStageOptions = ({
  enabled = true,
  filters = {},
  probe,
  stage,
  updateFilters,
}: UsePreservedStageOptionsArgs) => {
  const shouldRestoreLastOptionsRef = useRef(true);
  const isMount = useRef(true);
  const location = useLocation();
  const [shouldUsePreservedOptions] = useState(() => !location.search.includes('stage-filter'));
  const stageOptions = pick(get(savedOptions, [probe.label, 'stageFilters', stage.name]), stageOptionFields);
  const dataSource = isMount.current ? stageOptions.dataSource : filters.dataSource;
  const probeTimeOptions = pick(get(savedOptions, [probe.label, 'filter']), probeTimeOptionFields);
  const shouldRestoreTimeOptions = 'timeSeriesDuration' in filters && 'timeSeriesAggregation' in filters;

  const restoredOptions = useMemo(() => {
    if (!shouldUsePreservedOptions) return null;
    const result = omit(
      merge(
        {},
        shouldRestoreTimeOptions ? probeTimeOptions : {},
        stageOptions
      ),
      isMount.current ? [] : ['dataSource']
    );
    if (dataSource !== stageOptions.dataSource) {
      return {};
    }
    if (shouldRestoreTimeOptions && (stageOptions.aggregationType === 'none')) {
      return {...result, timeSeriesAggregation: 0};
    }

    return result;
  }, [dataSource, probeTimeOptions, shouldRestoreTimeOptions, shouldUsePreservedOptions, stageOptions]);

  useLayoutEffect(() => {
    const restoredFilters = {...filters, ...restoredOptions};
    if (enabled && shouldRestoreLastOptionsRef.current &&
      restoredOptions && !isEmpty(restoredOptions) && shouldUsePreservedOptions
    ) {
      updateFilters(restoredFilters);
    }
    set(savedOptions, [probe.label, 'stageFilters', stage.name], restoredFilters);
    shouldRestoreLastOptionsRef.current = false;
    isMount.current = false;
  }, [filters, restoredOptions, probe.label, shouldUsePreservedOptions, stage.name, updateFilters, enabled]
  );

  return useCallback((newFilters, options) => {
    if (newFilters.dataSource === filters.dataSource) {
      set(savedOptions, [probe.label, 'stageFilters', stage.name], newFilters);
      if ('timeSeriesDuration' in newFilters && 'timeSeriesAggregation' in newFilters) {
        set(savedOptions, [probe.label, 'filter'], {
          timeSeriesDuration: newFilters.timeSeriesDuration || probeTimeOptions.timeSeriesDuration,
          timeSeriesAggregation: newFilters.timeSeriesAggregation || probeTimeOptions.timeSeriesAggregation,
        });
      }
    } else {
      shouldRestoreLastOptionsRef.current = true;
    }
    updateFilters(newFilters, options);
  }, [
    filters.dataSource, probe.label, probeTimeOptions.timeSeriesAggregation,
    probeTimeOptions.timeSeriesDuration, stage.name, updateFilters
  ]);
};
